import { Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CallerService } from '../../services/caller.service'
import { PpaModel, ReviewModel } from '../../ngrx-store/app.state'
import { ppaPlace } from '../../utils/ppa.util'
import { PpaReviewPager } from '../../utils/ppa-review-pager'

@Component({
    selector: 'app-more-view-search',
    templateUrl: './more-view-search.component.html',
    styleUrls: ['./more-view-search.component.css'],
})
export class MoreViewSearchComponent implements OnInit, OnDestroy {
    /** "Ikeja, Lagos" - as the PPA card and page show it. */
    readonly ppaPlace = ppaPlace

    /** The newest page of reviews, then "Show more reviews" for the rest (GET /ppas/:id/reviews). */
    readonly reviewPager = new PpaReviewPager((before) => this.callerService.getPpaReviews(this.ppaKey, before))

    loading = false
    loadFailed = false

    private destroy$ = new Subject<void>()

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { ppa: PpaModel | any },
        private moreViewDialogRef: MatDialogRef<MoreViewSearchComponent>,
        private callerService: CallerService,
        private host: ElementRef<HTMLElement>,
    ) {}

    ngOnInit(): void {
        // The search lists (/search, /all-items) carry each PPA's first page of reviews and its cursor;
        // GET /ppas doesn't send any - fetch the first page.
        if (this.data.ppa?.Reviews) {
            this.reviewPager.start(this.data.ppa.Reviews, this.data.ppa.reviews_next_cursor)
        } else {
            this.loadFirstPage()
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    loadFirstPage(): void {
        this.loading = true
        this.loadFailed = false
        this.callerService.getPublicPpa(this.ppaKey).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
                const ppa = res?.body?.ppa
                if (ppa) {
                    this.data.ppa = { ...this.data.ppa, ...ppa }
                    this.reviewPager.start(ppa.Reviews, ppa.reviews_next_cursor)
                } else {
                    this.loadFailed = true
                }
                this.loading = false
            },
            error: () => {
                this.loadFailed = true
                this.loading = false
            },
        })
    }

    /** All its reviews, for "10 of 34" - from the stats, else what's loaded. */
    get reviewCount(): number | null {
        const count = this.data.ppa?.review_count
        return count === undefined || count === null ? null : Number(count)
    }

    /** After "Show more reviews": focus the first new one, so a keyboard or screen reader picks up from there. */
    focusReview(added: ReviewModel[]): void {
        if (!added.length) return
        setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(`[data-review-id="${added[0].id}"]`)?.focus())
    }

    close(): void {
        this.moreViewDialogRef.close()
    }

    trackByReviewId(_index: number, review: ReviewModel): number {
        return review.id
    }

    private get ppaKey(): string {
        return this.data.ppa.slug_id ?? String(this.data.ppa.id)
    }
}
